import dayjs from 'dayjs';
import utcPlugin from 'dayjs/plugin/utc';
import dayOfYear from 'dayjs/plugin/dayOfYear';
import enLocale from 'dayjs/locale/en';
import { enums } from '..';
import { shortResolutionFormatter } from './resolutionFormatter';

dayjs.extend(utcPlugin);
dayjs.extend(dayOfYear);

const localeOptions = {
  ...enLocale,
  weekStart: 1,
};

/**
 * Round up or truncate date to closest acceptable value
 * @param date
 * @param timeframe
 * @param multiplier
 * @returns
 */
export const formatDateByTimeframe = (
  date: number,
  timeframe: enums.EParsedTimeframes,
  multiplier = 1000
) => {
  const formatedDate = dayjs(date * multiplier)
    .locale(localeOptions)
    .utcOffset(0);
  switch (timeframe) {
    case enums.EParsedTimeframes.second:
      return formatedDate.startOf('second').valueOf();
    case enums.EParsedTimeframes.fiveSeconds:
      return formatedDate
        .set('milliseconds', 0)
        .subtract(formatedDate.second() % 5, 'seconds')
        .valueOf();
    case enums.EParsedTimeframes.tenSeconds:
      return formatedDate
        .set('milliseconds', 0)
        .subtract(formatedDate.second() % 10, 'seconds')
        .valueOf();
    case enums.EParsedTimeframes.minute:
      return formatedDate.startOf('minute').valueOf();
    case enums.EParsedTimeframes.threeMinutes:
      return formatedDate
        .set('seconds', 0)
        .set('milliseconds', 0)
        .subtract(formatedDate.minute() % 3, 'minutes')
        .valueOf();
    case enums.EParsedTimeframes.fiveMinutes:
      return formatedDate
        .set('seconds', 0)
        .set('milliseconds', 0)
        .subtract(formatedDate.minute() % 5, 'minutes')
        .valueOf();
    case enums.EParsedTimeframes.fifteenMinutes:
      return formatedDate
        .set('seconds', 0)
        .set('milliseconds', 0)
        .subtract(formatedDate.minute() % 15, 'minutes')
        .valueOf();
    case enums.EParsedTimeframes.thirtyMinutes:
      return formatedDate
        .set('seconds', 0)
        .set('milliseconds', 0)
        .subtract(formatedDate.minute() % 30, 'minutes')
        .valueOf();
    case enums.EParsedTimeframes.hour:
      return formatedDate.utc().startOf('hour').valueOf();
    case enums.EParsedTimeframes.twoHours:
      return formatedDate
        .utc()
        .set('minutes', 0)
        .set('seconds', 0)
        .set('milliseconds', 0)
        .subtract(formatedDate.hour() % 2, 'hours')
        .valueOf();
    case enums.EParsedTimeframes.fourHours:
      return formatedDate
        .utc()
        .set('minutes', 0)
        .set('seconds', 0)
        .set('milliseconds', 0)
        .subtract(formatedDate.hour() % 4, 'hours')
        .valueOf();
    case enums.EParsedTimeframes.sixHours:
      return formatedDate
        .utc()
        .set('minutes', 0)
        .set('seconds', 0)
        .set('milliseconds', 0)
        .subtract(formatedDate.hour() % 6, 'hours')
        .valueOf();
    case enums.EParsedTimeframes.eightHours:
      return formatedDate
        .utc()
        .set('minutes', 0)
        .set('seconds', 0)
        .set('milliseconds', 0)
        .subtract(formatedDate.hour() % 8, 'hours')
        .valueOf();
    case enums.EParsedTimeframes.twelveHours:
      return formatedDate
        .utc()
        .set('minutes', 0)
        .set('seconds', 0)
        .set('milliseconds', 0)
        .subtract(formatedDate.hour() % 12, 'hours')
        .valueOf();
    case enums.EParsedTimeframes.day:
      return formatedDate.startOf('day').valueOf();
    case enums.EParsedTimeframes.threeDays:
      const yearDay = formatedDate.dayOfYear();
      const remainder = yearDay % 3;
      return formatedDate
        .set('hours', 0)
        .set('minutes', 0)
        .set('seconds', 0)
        .set('milliseconds', 0)
        .subtract(remainder ? remainder - 1 : 2, 'days')
        .valueOf();
    case enums.EParsedTimeframes.week:
      return formatedDate.locale('en').startOf('week').valueOf();
    case enums.EParsedTimeframes.month:
      return formatedDate.startOf('month').valueOf();
    case enums.EParsedTimeframes.year:
      return formatedDate.startOf('year').valueOf();
    default:
      return formatedDate.startOf('hour').valueOf();
  }
};

export const mapInterval = (interval: enums.ETimeframes) => {
  return shortResolutionFormatter(interval);
};
